import { getEnvVars } from '@environment';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { Query, QueryClient } from '@tanstack/react-query';
import { AsyncStorage } from '@tanstack/react-query-persist-client';
import { toast } from '@unistocks/components/toast';
import {
  setAbpApplicationConfigurationGetDefaultOptions
} from '@unistocks/proxy/axios-client/AbpApplicationConfigurationQuery';
import {
  setAbpApplicationLocalizationGetDefaultOptions
} from '@unistocks/proxy/axios-client/AbpApplicationLocalizationQuery';
import {
  setAssetsGetChartByIdDefaultOptions,
  setAssetsGetChartDefaultOptions
} from '@unistocks/proxy/axios-client/AssetsQuery';
import {
  setDashboardGetAssetListDataDefaultOptions,
  setDashboardGetCalendarDataDefaultOptions,
  setDashboardGetEventViewDataDefaultOptions,
  setDashboardGetItemDataDefaultOptions,
  setDashboardGetNetSavingsDataDefaultOptions
} from '@unistocks/proxy/axios-client/DashboardQuery';
import {
  setExchangeRatesGetExchangeRatesDefaultOptions
} from '@unistocks/proxy/axios-client/ExchangeRatesQuery';
import {
  setExternalAccountsGetAccountsDefaultOptions,
  setExternalAccountsGetPortfoliosDefaultOptions,
  setExternalAccountsGetProvidersDefaultOptions
} from '@unistocks/proxy/axios-client/ExternalAccountsQuery';
import {
  setPortfoliosGetDefaultOptions,
  setPortfoliosGetTimeSeriesDefaultOptions
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  setTimeZoneSettingsGetTimezonesDefaultOptions
} from '@unistocks/proxy/axios-client/TimeZoneSettingsQuery';
import {
  setWatchlistsGetDefaultOptions
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { STORAGE_ID } from '@unistocks/shared/consts';
import { storage } from '@unistocks/shared/storage';
import { isHandledError } from '@unistocks/shared/utils/error';
import { Duration } from 'luxon';

const { production } = getEnvVars();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ONE_MINUTE = Duration.fromDurationLike({ minutes: 1 }).toMillis();
const FIVE_MINUTES = Duration.fromDurationLike({ minutes: 5 }).toMillis();
const FIFTEEN_MINUTES = Duration.fromDurationLike({ minutes: 15 }).toMillis();
const ONE_HOUR = Duration.fromDurationLike({ hours: 1 }).toMillis();
const ONE_DAY = Duration.fromDurationLike({ days: 1 }).toMillis();
const ONE_WEEK = Duration.fromDurationLike({ days: 7 }).toMillis();

export const DEFAULT_CACHE_TIME = ONE_WEEK;
export const DEFAULT_STALE_TIME = FIVE_MINUTES;
export const ASSET_PRICE_STALE_TIME = FIFTEEN_MINUTES;
export const DEFAULT_RETRY_COUNT = 3;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: production ? DEFAULT_STALE_TIME : 0,
      gcTime: DEFAULT_CACHE_TIME,
      retry: DEFAULT_RETRY_COUNT
    },
    mutations: {
      onError: e => {
        if (isHandledError(e)) {
          toast.error(e.error.message, { duration: 10_000 });
        }
      }
    }
  }
});

const clientStorage: AsyncStorage = {
  setItem: (key, value) => {
    storage.set(key, value);
  },
  getItem: key => {
    const value = storage.getString(key);
    return value === undefined ? null : value;
  },
  removeItem: key => {
    storage.remove(key);
  },
  entries: () => {
    const allKeys = storage.getAllKeys();
    const entries: [string, string][] = [];

    for (const key of allKeys) {
      const value = storage.getString(key);
      if (value !== undefined) {
        entries.push([key, value]);
      }
    }

    return entries;
  }
};

export const queryClientPersister = createAsyncStoragePersister({
  storage: clientStorage,
  key: `${STORAGE_ID}:query-client`,
  throttleTime: 1000
});

export const setQueryDefaults = () => {
  setAbpApplicationConfigurationGetDefaultOptions({
    staleTime: ONE_DAY,
    gcTime: Infinity
  });

  setAbpApplicationLocalizationGetDefaultOptions({
    staleTime: ONE_DAY,
    gcTime: Infinity
  });

  setPortfoliosGetDefaultOptions({
    staleTime: ASSET_PRICE_STALE_TIME
  });

  setWatchlistsGetDefaultOptions({
    staleTime: ASSET_PRICE_STALE_TIME
  });

  // Query key varies by selected dates, which can store a lot of data
  setPortfoliosGetTimeSeriesDefaultOptions({
    gcTime: ONE_HOUR
  });

  setAssetsGetChartDefaultOptions({
    staleTime: FIVE_MINUTES,
    gcTime: FIFTEEN_MINUTES
  });

  setAssetsGetChartByIdDefaultOptions({
    staleTime: FIVE_MINUTES,
    gcTime: FIFTEEN_MINUTES
  });

  setDashboardGetItemDataDefaultOptions({
    staleTime: ASSET_PRICE_STALE_TIME
  });

  setDashboardGetCalendarDataDefaultOptions({
    staleTime: ONE_HOUR * 12
  });

  setDashboardGetAssetListDataDefaultOptions({
    staleTime: ASSET_PRICE_STALE_TIME
  });

  setDashboardGetNetSavingsDataDefaultOptions({
    staleTime: ASSET_PRICE_STALE_TIME
  });

  setDashboardGetEventViewDataDefaultOptions({
    staleTime: ASSET_PRICE_STALE_TIME
  });

  setExchangeRatesGetExchangeRatesDefaultOptions({
    staleTime: ONE_DAY
  });

  // External accounts
  setExternalAccountsGetAccountsDefaultOptions({
    staleTime: 0
  });

  setExternalAccountsGetPortfoliosDefaultOptions({
    staleTime: 0
  });

  setExternalAccountsGetProvidersDefaultOptions({
    staleTime: ONE_DAY
  });

  // Profile
  setTimeZoneSettingsGetTimezonesDefaultOptions({
    staleTime: ONE_WEEK,
    gcTime: Infinity
  });
};

export type QueryPredicateFactory = (id: string) => (query: Query) => boolean;

/**
 * Query predicate to filter queries with query key containing the given string.
 * @param id
 */
export const queryPredicateByKey: QueryPredicateFactory = (key: string) => query =>
  query.queryKey.includes(key);
